/**
 * Меню «Обучение» внизу боковой панели (E4.6b, макет training-menu): продолжить урок,
 * быстрое знакомство, урок для текущей страницы, все сценарии и демонстрация.
 */

import * as MenuPrimitive from '@radix-ui/react-dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { contextLesson } from './contextLesson';
import { exerciseResumable, useExerciseStore } from './exerciseStore';
import { LIVE_MODE } from './types';
import { useTrainingExercise } from './useTrainingExercise';
import { useTrainingProgress } from './useTrainingProgress';
import { useTrainingTour } from './useTrainingTour';

export function TrainingMenu() {
	const { t } = useTranslation('learning');
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { progress } = useTrainingProgress();
	const { tour, startLesson, resumeLesson } = useTrainingTour();
	const { mode } = useWorkspaceDataSource();
	const { resume: resumeExercise } = useTrainingExercise();
	/* Продолжение задания -- всегда в учебном примере, на странице оставшегося действия */
	const pausedExercise = useExerciseStore((state) => exerciseResumable(state, mode === LIVE_MODE));
	const last = progress.last;
	const resumable = last !== null && progress.lessons[last]?.status !== 'completed';

	return (
		<MenuPrimitive.Root>
			<MenuPrimitive.Trigger
				className={
					pathname === '/learning' ? 'ws-training-trigger ws-active' : 'ws-training-trigger'
				}
				aria-label={t('trigger.label')}
			>
				<WorkspaceIcon name="book" />
				<span className="ws-training-trigger-label">{t('trigger.label')}</span>
				<span className="ws-training-trigger-arrow" aria-hidden="true">
					⌃
				</span>
			</MenuPrimitive.Trigger>
			<MenuPrimitive.Portal>
				<MenuPrimitive.Content
					className="ws-training-menu"
					data-training-menu=""
					side="top"
					align="start"
					sideOffset={8}
				>
					<span className="ws-training-menu-caption">{t('menu.caption')}</span>
					{pausedExercise !== null && (
						<MenuPrimitive.Item onSelect={() => resumeExercise(pausedExercise)}>
							<WorkspaceIcon name="clock" />
							{t('exercise.resume', { title: t(`learning:exercises.${pausedExercise}.title`) })}
						</MenuPrimitive.Item>
					)}
					{tour.phase === 'paused' && tour.lesson !== null ? (
						<MenuPrimitive.Item onSelect={resumeLesson}>
							<WorkspaceIcon name="clock" />
							{t('menu.resume', { lesson: t(`learning:lessons.${tour.lesson}.short`) })}
						</MenuPrimitive.Item>
					) : (
						resumable && (
							<MenuPrimitive.Item onSelect={() => void startLesson(last)}>
								<WorkspaceIcon name="clock" />
								{t('menu.resume', { lesson: t(`learning:lessons.${last}.short`) })}
							</MenuPrimitive.Item>
						)
					)}
					<MenuPrimitive.Item onSelect={() => void startLesson('quick', { welcome: true })}>
						<WorkspaceIcon name="overview" />
						{t('menu.quick')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Item onSelect={() => void startLesson(contextLesson(pathname))}>
						<WorkspaceIcon name="report" />
						{t('menu.context')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Item onSelect={() => navigate('/learning')}>
						<WorkspaceIcon name="list" />
						{t('menu.all')}
					</MenuPrimitive.Item>
					<MenuPrimitive.Separator className="ws-training-menu-divider" />
					<MenuPrimitive.Item onSelect={() => void startLesson('demo', { restart: true })}>
						<WorkspaceIcon name="path" />
						{t('menu.demo')}
					</MenuPrimitive.Item>
				</MenuPrimitive.Content>
			</MenuPrimitive.Portal>
		</MenuPrimitive.Root>
	);
}
